import { SiteUrls } from '@tableau/shared-urls';
import {
  GenericFlyoutListMenuWidget,
  GenericMenuItemInfo,
  HorizontalOriginOption,
  MenuButtonWidget,
  VerticalOriginOption,
} from '@tableau/widgets';
import React from 'react';
import { defaultMemoize } from 'reselect';
import { Messages } from '../../../../../gen-src/Localize';
import { SvgSpacesSmallFlow, SvgSpacesSmallWorkbooks } from '../../../../../gen-src/icons';
import { ApiRequests, ValidTelemetryEvent } from '../../../data-catalog-shared';
import { generateCreateContentMenuItem } from './ContentMenuItems';
import { ContentType, CreateContentItemFromTelemetryEvent, DataType } from './CreateContentItemFromTelemetryEvent';

export const RESOURCE_TYPE_DATABASE = 'database';
export const RESOURCE_TYPE_TABLE = 'table';
export type ResourceType =
  | typeof RESOURCE_TYPE_DATABASE
  | typeof RESOURCE_TYPE_TABLE;

export const DOCUMENT_TYPE_FLOW = 'flow';
export const DOCUMENT_TYPE_WORKBOOK = 'workbook';
export type DocumentType =
  | typeof DOCUMENT_TYPE_FLOW
  | typeof DOCUMENT_TYPE_WORKBOOK;

/**
 * Will inform the typesystem that the parameter n should never be valid.  This is useful for
 * putting in the default of switch statements, as it will allow the type checker to verify that you
 * exhausted all the type possibilities in case statemeents.  You will then get a compile error, if someone
 * adds something that needs a new case statement, but one isn't there.
 *
 * @param n  the parameter passed to the switch statement.
 */
export function assertNeverHit(_n: never): never {
  throw new Error(
    '{0} is a case the type system thought was impossible to get to.  See if your types are right!'
  );
}

function createHandleNewFlowForDatabaseAction(
  databaseId: string,
  siteUrls: SiteUrls,
  newWindow: Window,
  databaseClass: string,
  sendCreateDocumentEvent?: (event?: Partial<Omit<ValidTelemetryEvent<CreateContentItemFromTelemetryEvent>, 'type'>> | undefined) => void,
  projectIdForNewDocuments?: string
) {
  return () => {
    newWindow.location.href = siteUrls.flowEditorFlowPath({
      flowLuid: 'new',
      graphQLDatabaseId: databaseId,
      projectId: projectIdForNewDocuments,
    });
    if (sendCreateDocumentEvent) {
      const sendEvent = {
        type: 'create_content_from_item',
        content_type: ContentType.Flow,
        data_type: DataType.Database,
        connection_type: databaseClass
      };

      sendCreateDocumentEvent(sendEvent);
    }
  }
}

function createHandleNewFlowForTableAction(
  tableId: string,
  siteUrls: SiteUrls,
  newWindow: Window,
  databaseClass: string,
  sendCreateDocumentEvent?: (event?: Partial<Omit<ValidTelemetryEvent<CreateContentItemFromTelemetryEvent>, 'type'>> | undefined) => void,
  projectIdForNewDocuments?: string
) {

  return () => {
    newWindow.location.href = siteUrls.flowEditorFlowPath({
      flowLuid: 'new',
      graphQLTableId: tableId,
      projectId: projectIdForNewDocuments,
    });
    if (sendCreateDocumentEvent) {
      const sendEvent = {
        type: 'create_content_from_item',
        content_type: ContentType.Flow,
        data_type: DataType.Table,
        connection_type: databaseClass
      };

      sendCreateDocumentEvent(sendEvent);
    }
  }
}

function createHandleNewWorkbookForTableAction(
  tableId: string,
  siteUrls: SiteUrls,
  newWindow: Window,
  databaseClass: string,
  sendCreateDocumentEvent?: (event?: Partial<Omit<ValidTelemetryEvent<CreateContentItemFromTelemetryEvent>, 'type'>> | undefined) => void
) {
  return () => {
    newWindow.location.href = siteUrls.newWorkbookFromTable(tableId);
    if (sendCreateDocumentEvent) {
      const sendEvent = {
        type: 'create_content_from_item',
        content_type: ContentType.Workbook,
        data_type: DataType.Table,
        connection_type: databaseClass
      };

      sendCreateDocumentEvent(sendEvent);
    }
  };
}

function createHandleNewWorkbookForDatabaseAction(
  databaseId: string,
  siteUrls: SiteUrls,
  newWindow: Window,
  databaseClass: string,
  sendCreateDocumentEvent?: (event?: Partial<Omit<ValidTelemetryEvent<CreateContentItemFromTelemetryEvent>, 'type'>> | undefined) => void
) {
  return () => {
    newWindow.location.href = siteUrls.newWorkbookFromDatabase(databaseId);
    if (sendCreateDocumentEvent) {
      const sendEvent = {
        type: 'create_content_from_item',
        content_type: ContentType.Workbook,
        data_type: DataType.Database,
        connection_type: databaseClass
      };

      sendCreateDocumentEvent(sendEvent);
    }
  };
}

function isRowEnabledForTableOrDatabase(
  connectionType: string | null,
  allowedClasses: ReadonlyArray<string>
) {
  return connectionType ? allowedClasses.indexOf(connectionType) > -1 : false;
}

function getCreateHandlerForDatabase(documentType: DocumentType) {
  switch (documentType) {
    case DOCUMENT_TYPE_FLOW:
      return defaultMemoize(createHandleNewFlowForDatabaseAction);
    case DOCUMENT_TYPE_WORKBOOK:
      return defaultMemoize(createHandleNewWorkbookForDatabaseAction);
    default:
      assertNeverHit(documentType);
  }
}

function getCreateHandlerForTable(documentType: DocumentType) {
  switch (documentType) {
    case DOCUMENT_TYPE_FLOW:
      return defaultMemoize(createHandleNewFlowForTableAction);
    case DOCUMENT_TYPE_WORKBOOK:
      return defaultMemoize(createHandleNewWorkbookForTableAction);
    default:
      assertNeverHit(documentType);
  }
}

function getCreateHandler(
  resourceType: ResourceType,
  documentType: DocumentType
) {
  switch (resourceType) {
    case RESOURCE_TYPE_DATABASE:
      return getCreateHandlerForDatabase(documentType);
    case RESOURCE_TYPE_TABLE:
      return getCreateHandlerForTable(documentType);
    default:
      assertNeverHit(resourceType);
  }
}

export type CreateDocumentFromResourceButtonProps = {
  readonly sendCreateDocumentEvent: (event?: Partial<Omit<ValidTelemetryEvent<CreateContentItemFromTelemetryEvent>, 'type'>> | undefined) => void
  readonly apiRequests: ApiRequests;
  readonly siteUrls: SiteUrls;
  readonly isEmbedded: boolean;
  readonly resourceId: string;
  readonly resourceType: ResourceType;
  readonly databaseClass: string | null;
  readonly testId: string;
  readonly title: string;
  readonly canCreateWorkbook: boolean;
  readonly canCreateFlow: boolean;
  readonly projectIdForNewDocuments?: string;
};

export type CreateDocumentFromResourceButtonState = {
  readonly isSupportedDatabaseClass: boolean;
  readonly isMenuOpen: boolean;
};

export class CreateDocumentFromResourceButton extends React.PureComponent<
  CreateDocumentFromResourceButtonProps,
  CreateDocumentFromResourceButtonState
> {
  private createButtonRef: React.RefObject<
    HTMLButtonElement
  > = React.createRef();

  public constructor(props: CreateDocumentFromResourceButtonProps) {
    super(props);
    this.state = {
      isSupportedDatabaseClass: false,
      isMenuOpen: false,
    };
  }

  public override componentDidMount() {
    this.props.apiRequests
      .getAllowedClasses({ federatableOnly: 'false' })
      .then((r) => {
        const isSupported: boolean = isRowEnabledForTableOrDatabase(
          this.props.databaseClass,
          r.allowedClasses
        );
        this.setState({
          isSupportedDatabaseClass: isSupported,
          isMenuOpen: false,
        });
      })
      .catch((_) => {
        this.setState({
          isSupportedDatabaseClass: false,
          isMenuOpen: false,
        });
      }); // Leave the state as the default
  }

  private toggleMenu = () => {
    this.setState({
      isMenuOpen: !this.state.isMenuOpen,
    });
  };

  private hideMenu = () => {
    this.setState({
      isMenuOpen: false,
    });
  };

  private menuSelect = (value: string) => {
    this.hideMenu();
    const newWindow: Window | null = window.open('', '_blank');
    const databaseClass: string = this.props.databaseClass ? this.props.databaseClass : '';
    const sendCreateEvent = this.props.sendCreateDocumentEvent ? this.props.sendCreateDocumentEvent : undefined;
    if (newWindow) {
      let createHandler;

      if (value === DOCUMENT_TYPE_WORKBOOK) {
        createHandler = getCreateHandler(this.props.resourceType, DOCUMENT_TYPE_WORKBOOK)(
          this.props.resourceId,
          this.props.siteUrls,
          newWindow,
          databaseClass,
          sendCreateEvent
        );
      } else if (value === DOCUMENT_TYPE_FLOW) {
        createHandler = getCreateHandler(this.props.resourceType, DOCUMENT_TYPE_FLOW)(
          this.props.resourceId,
          this.props.siteUrls,
          newWindow,
          databaseClass,
          sendCreateEvent,
          this.props.projectIdForNewDocuments
        );
      }

      if (createHandler != null && createHandler !== undefined) {
        createHandler();
      }
    }
  };

  private getDocumentTitle = (docType: DocumentType) => {
    switch (docType) {
      case DOCUMENT_TYPE_FLOW:
        return Messages.Flow();
      case DOCUMENT_TYPE_WORKBOOK:
        return Messages.Workbook();
      default:
        assertNeverHit(docType);
    }
  };

  private getResourceUsageTitle = (resType: ResourceType) => {
    switch (resType) {
      case 'database':
        return Messages.UsingThisDatabaseTitle();
      case 'table':
        return Messages.UsingThisTableTitle();
      default:
        assertNeverHit(resType);
    }
  };

  private generateMenuItems = (): GenericMenuItemInfo[] => {
    const workbookIcon = <SvgSpacesSmallWorkbooks />;
    const flowIcon = <SvgSpacesSmallFlow />;
    const menuItems: GenericMenuItemInfo[] = [];
    const isWorkbookCreationDisabled = !this.props.canCreateWorkbook;
    const isFlowCreationDisabled = !this.props.canCreateFlow;
    menuItems.push(
      {
        value: DOCUMENT_TYPE_WORKBOOK,
        keyboardAccessibleValue: Messages.Workbook(),
        displayFormatterBoundWithData: generateCreateContentMenuItem(
          this.getDocumentTitle(DOCUMENT_TYPE_WORKBOOK),
          this.getResourceUsageTitle(this.props.resourceType),
          workbookIcon,
          'workbook',
          isWorkbookCreationDisabled
        ),
        disabled: isWorkbookCreationDisabled,
      },
      {
        value: DOCUMENT_TYPE_FLOW,
        keyboardAccessibleValue: Messages.Flow(),
        displayFormatterBoundWithData: generateCreateContentMenuItem(
          this.getDocumentTitle(DOCUMENT_TYPE_FLOW),
          this.getResourceUsageTitle(this.props.resourceType),
          flowIcon,
          'flow',
          isFlowCreationDisabled
        ),
        disabled: isFlowCreationDisabled,
      }
    );
    return menuItems;
  };

  private renderMenu = () => {
    return (
      <GenericFlyoutListMenuWidget
        allowAutoPosition={true}
        anchorEl={this.createButtonRef.current}
        anchorOrigin={{
          vertical: VerticalOriginOption.bottom,
          horizontal: HorizontalOriginOption.left,
        }}
        menuItemInfos={this.generateMenuItems()}
        onSelect={this.menuSelect}
        requestCloseAll={this.hideMenu}
        id={'create-content-buttons-menu'}
        testId={'create-content-buttons-menu'}
        toggleEl={this.createButtonRef.current}
        menuStyle={{ minWidth: '150px', whiteSpace: 'nowrap' }}
      />
    );
  };

  private getTitleText = () => {
    // If both methods are disabled then we show the unsupported row warning.
    return !this.props.canCreateFlow && !this.props.canCreateWorkbook
      ? Messages.ConnectToUnsupportedRowWarningTextDefault()
      : undefined;
  };

  public override render() {
    return (
      <div title={this.getTitleText()}>
        <MenuButtonWidget
          aria-haspopup={'menu'}
          aria-controls={'create-content-buttons-panel'}
          onClick={this.toggleMenu}
          ref={this.createButtonRef}
          testId={'create-content-button'}
          disabled={!this.props.canCreateFlow && !this.props.canCreateWorkbook}
        >
          {Messages.New()}
          {this.state.isMenuOpen ? this.renderMenu() : null}
        </MenuButtonWidget>
      </div>
    );
  }
}
